/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client'

import './ProductImageGalleryStyles.css'

import clsx from 'clsx'
import { AnimatePresence, LazyMotion, m } from 'framer-motion'
import {
  type ComponentProps,
  ElementRef,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
// @ts-ignore
import { MOUSE_ACTIVATION, TOUCH_ACTIVATION } from 'react-input-position-v2'
import Slider, { Settings } from 'react-slick'

import Icon from '../../components/Icon'
import MediaMagnifier from '../../components/MediaMagnifier'
import { PayloadImage } from '../../components/PayloadImage'
import { PayloadVideo } from '../../components/PayloadVideo'
import Tag from '../../components/Tag'
import { isValidTag } from '../../helpers/checks'
import isPayloadImage from '../../helpers/isPayloadImage'
import modalService from '../../service/modalService'
import { Media } from '../../types'
import ProductImageGallery, { ProductImageGalleryProps } from './ProductImageGallery'

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

type ProductImageSlideProps = NonNullable<ProductImageGalleryProps['galleryItems']>[number] & {
  isActive: boolean
  onActivateZoom: () => void
  onDeactivateZoom: () => void
}

export default function DesktopGallery({
  data,
  loading,
}: ComponentProps<typeof ProductImageGallery>) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSwipeSliderEnable, setIsSwipeSliderEnable] = useState(true)

  const modalRef = useRef<ElementRef<'dialog'>>(null)
  const sliderRef = useRef<Slider>(null)

  const disableSliderSwap = useCallback(() => {
    setIsSwipeSliderEnable(false)
  }, [])

  const enableSliderSwap = useCallback(() => {
    setIsSwipeSliderEnable(true)
  }, [])

  const sliderSettings: Settings = useMemo(
    () => ({
      swipeToSlide: isSwipeSliderEnable,
      draggable: isSwipeSliderEnable,
      swipe: isSwipeSliderEnable,
      touchMove: isSwipeSliderEnable,
      slidesToScroll: 1,
      speed: 500,
      arrows: false,
      slidesToShow: 1,
      infinite: true,
      dots: true,
      beforeChange: (_, next) => {
        setCurrentSlide(next)
      },
      // @ts-ignore
      appendDots: dots => <DotsContainer dots={dots} />,
      customPaging: index => (
        <Dot
          isActive={currentSlide === index}
          slideNumber={index + 1}
          onClick={() => {
            sliderRef.current?.slickGoTo(index)
          }}
          media={(data?.galleryItems ?? [])[index]?.media}
        />
      ),
      className: 'product-image-gallery',
    }),
    [currentSlide, data?.galleryItems, isSwipeSliderEnable],
  )

  const openModal = useCallback((imageIndex: number) => {
    modalService.changeModalState('productGallery', true)
    modalRef.current?.showModal()
    sliderRef.current?.slickGoTo(imageIndex)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    modalService.changeModalState('productGallery', false)
    modalRef.current?.close()
    setIsModalOpen(false)
  }, [])

  const onModalClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>) => {
      if (!modalRef.current) return

      // check if we click on backdrop
      const rect = modalRef.current.getBoundingClientRect()

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        closeModal()
      }
    },
    [closeModal],
  )

  useEffect(() => {
    modalService.addActionOnScreenChange('productGallery', {
      onCloseMobile() {
        modalRef.current?.close()
        setIsModalOpen(false)
      },
    })
  }, [])

  if (loading || !data?.galleryItems) {
    return (
      <LazyMotion features={motionFeatures} strict>
        <div className='hidden flex-col gap-3 xl:flex'>
          <div className='radius-md relative isolate h-[560px] overflow-hidden'>
            <AnimatePresence>
              <m.div
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='absolute inset-0 h-[560px] w-full animate-pulse bg-gray'
              />
            </AnimatePresence>
          </div>

          <div className='flex gap-3'>
            {Array.from({ length: 2 }).map((_, idx) => (
              <div
                key={idx}
                className='radius-md relative h-[560px] basis-1/2 overflow-hidden focus-visible:outline focus-visible:outline-offset-1'
              >
                <AnimatePresence>
                  <m.div
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='absolute inset-0 h-[560px] w-full animate-pulse bg-gray'
                  />
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </LazyMotion>
    )
  }

  const { galleryItems } = data

  if (!galleryItems.length) {
    return null
  }

  return (
    <>
      <div className='hidden flex-col gap-3 xl:flex'>
        <div className='radius-md relative isolate h-[560px] overflow-hidden'>
          {isValidTag(galleryItems[0].tag) && (
            <div className='absolute left-4 top-4 z-10'>
              <Tag
                data={{ text: galleryItems[0].tag.text, color: 'accent' }}
                icon={galleryItems[0].tag.icon}
                rounded={false}
              />
            </div>
          )}

          <button
            onClick={() => openModal(0)}
            className='w-full focus-visible:outline focus-visible:outline-offset-1'
          >
            {isPayloadImage(galleryItems[0].media) ? (
              <PayloadImage
                priority
                sizes='calc(100vw - 555px)'
                src={galleryItems[0].media}
                objectFit='cover'
                className='!h-[560px] w-full'
              />
            ) : (
              <PayloadVideo
                src={galleryItems[0].media}
                enableControls
                className='h-[560px] w-full object-cover'
              />
            )}
          </button>
        </div>

        <div className='flex gap-3'>
          {galleryItems.slice(1, 3).map(({ media, tag }, index) => (
            <div
              role='button'
              key={index}
              className={clsx(
                'radius-md relative h-[560px] overflow-hidden focus-visible:outline focus-visible:outline-offset-1',
                galleryItems[2] != null ? 'basis-1/2' : 'w-full',
              )}
              tabIndex={0}
              onClick={() => openModal(index + 1)}
            >
              {tag != null && (
                <div className='absolute left-4 top-4'>
                  <Tag data={{ text: tag.text, color: 'accent' }} icon={tag.icon} rounded={false} />
                </div>
              )}

              {isPayloadImage(media) ? (
                <PayloadImage
                  src={media}
                  sizes='calc(100vw - 555px)'
                  objectFit='cover'
                  className='!h-[560px] w-full'
                />
              ) : (
                <PayloadVideo
                  src={media}
                  enableControls
                  playAutomatically
                  className='h-[560px] w-full object-cover'
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <dialog
        ref={modalRef}
        onCancel={closeModal}
        onClick={onModalClick}
        className='hidden max-h-none max-w-none bg-transparent backdrop:cursor-pointer backdrop:bg-darkblack backdrop:bg-opacity-90 xl:block'
      >
        <div
          role='button'
          className={clsx(
            'relative h-screen w-[100vw] bg-transparent',
            isModalOpen ? 'block' : 'hidden',
          )}
          onClick={closeModal}
          tabIndex={-1}
        >
          <button onClick={closeModal} className='absolute right-20 top-20 text-white'>
            <Icon name='MdClose' size={40} />
          </button>

          <div
            role='button'
            onClick={event => {
              event.stopPropagation()
            }}
            className='absolute left-0 right-0 top-1/2 w-full -translate-y-1/2 px-20'
            tabIndex={-1}
          >
            {/* @ts-ignore */}
            <Slider {...sliderSettings} ref={sliderRef}>
              {galleryItems.map((props, index) => (
                <ProductImageSlide
                  {...props}
                  key={index}
                  isActive={currentSlide === index}
                  onActivateZoom={disableSliderSwap}
                  onDeactivateZoom={enableSliderSwap}
                />
              ))}
            </Slider>
          </div>
        </div>
      </dialog>
    </>
  )
}

function ProductImageSlide({
  media,
  tag,
  isActive,
  onActivateZoom,
  onDeactivateZoom,
}: ProductImageSlideProps) {
  const renderMedia = useMemo(() => {
    if (isPayloadImage(media)) {
      return (
        <>
          <div className={clsx('w-full', isActive ? 'visible' : 'invisible')}>
            <MediaMagnifier
              mouseActivationMethod={MOUSE_ACTIVATION.CLICK}
              touchActivationMethod={TOUCH_ACTIVATION.DOUBLE_TAP}
              Component={PayloadImage}
              componentProps={{
                sizes: '100vw',
                src: media,
                className: 'h-halfScreen w-full',
                imgProps: {
                  className: 'object-cover',
                },
              }}
              onActivate={onActivateZoom}
              onDeactivate={onDeactivateZoom}
            />
          </div>

          <div className={clsx('absolute flex w-full', isActive ? 'invisible' : 'visible')}>
            <PayloadImage
              src={media}
              sizes='100vw'
              className='h-halfScreen w-full'
              imgProps={{
                className: 'object-cover',
              }}
            />
          </div>
        </>
      )
    } else {
      return (
        <PayloadVideo src={media} enableControls className='h-halfScreen w-full object-cover' />
      )
    }
  }, [isActive, media, onActivateZoom, onDeactivateZoom])

  return (
    <div className='radius-md relative isolate flex h-halfScreen overflow-hidden'>
      {tag != null && (
        <div className='absolute left-4 top-4 z-10'>
          <Tag data={{ text: tag.text, color: 'accent' }} icon={tag.icon} rounded={false} />
        </div>
      )}

      {renderMedia}
    </div>
  )
}

const DotsContainer = ({ dots }: { dots: React.ReactNode }) => {
  return (
    <div className='mt-4 flex justify-center'>
      <ul className='grid auto-cols-auto grid-flow-col gap-2'>{dots}</ul>
    </div>
  )
}

const Dot = ({
  isActive,
  onClick,
  slideNumber,
  media,
}: {
  isActive: boolean
  onClick: () => void
  slideNumber: number
  media?: Media
}) => {
  return (
    <button
      onClick={onClick}
      aria-label={`Navigate to ${slideNumber} slide`}
      className='flex w-full'
    >
      {isPayloadImage(media) ? (
        <PayloadImage
          src={media}
          className={clsx(
            'radius-md h-20 w-20 overflow-hidden !transition-opacity',
            isActive ? 'opacity-100' : 'opacity-50',
          )}
          imgProps={{
            className: 'object-cover',
          }}
          sizes='80px'
        />
      ) : (
        <PayloadVideo
          src={media}
          className={clsx(
            'radius-md h-20 w-20 overflow-hidden object-cover !transition-opacity',
            isActive ? 'opacity-100' : 'opacity-50',
          )}
        />
      )}
    </button>
  )
}
